import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

const VARIANTS = [
  { p: 3, q: -2, shift: 1, gZero: 0 },
  { p: 2, q: -4, shift: 1, gZero: -1 },
  { p: -2, q: -3, shift: -1, gZero: 0 },
  { p: 1, q: -2, shift: 2, gZero: -2 },
  { p: -3, q: -4, shift: -1, gZero: 2 },
  { p: 4, q: -3, shift: 2, gZero: 0 },
  { p: 2, q: -1, shift: -1, gZero: 0 },
  { p: -1, q: -2, shift: 1, gZero: 2 },
];

function gcd(first, second) {
  return second === 0 ? Math.abs(first) : gcd(second, first % second);
}

function fracLatex(value) {
  if (Number.isInteger(value)) return String(value);
  for (let denominator = 2; denominator <= 100; denominator += 1) {
    const numerator = Math.round(value * denominator);
    if (Math.abs(value - numerator / denominator) < 1e-9) {
      const divisor = gcd(Math.abs(numerator), denominator);
      const reducedNumerator = numerator / divisor;
      const reducedDenominator = denominator / divisor;
      const sign = reducedNumerator < 0 ? "-" : "";
      return `${sign}\\frac{${Math.abs(reducedNumerator)}}{${reducedDenominator}}`;
    }
  }
  return String(Number(value.toFixed(6))).replace(".", "{,}");
}

function signed(value, variable = "") {
  if (Math.abs(value) < 1e-9) return "";
  const abs = Math.abs(value);
  const body = `${abs === 1 && variable ? "" : fracLatex(abs)}${variable}`;
  return `${value > 0 ? "+" : "-"}${body}`;
}

function coefficient(value, variable) {
  if (value === 1) return variable;
  if (value === -1) return `-${variable}`;
  return `${fracLatex(value)}${variable}`;
}

function shiftedVariable(center) {
  if (center === 0) return "x";
  return center > 0 ? `x-${center}` : `x+${Math.abs(center)}`;
}

function shiftedArgument(shift) {
  if (shift === 0) return "x";
  return shift > 0 ? `x+${shift}` : `x-${Math.abs(shift)}`;
}

function evaluatedDifference(value, center) {
  const left = value < 0 ? `(${value})` : String(value);
  if (center === 0) return left;
  return center > 0 ? `${left}-${center}` : `${left}+${Math.abs(center)}`;
}

function quadraticLatex({ A, B, C }) {
  return `${coefficient(A, "x^2")}${signed(B, "x")}${signed(C)}`;
}

function makeTaskData(base) {
  const shiftedVertex = base.p - base.shift;
  const zeroDistance = base.gZero - shiftedVertex;
  const a = -base.q / (zeroDistance * zeroDistance);
  const A = a;
  const B = -2 * a * base.p;
  const C = a * base.p * base.p + base.q;
  return { ...base, a, A, B, C, maxPoints: 4 };
}

export function generateTask(options = {}) {
  const isOriginal = options.variant === "original" && options.session === "2026_MAJ";
  const base = isOriginal
    ? VARIANTS[0]
    : VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  return {
    type: "MaturaQuadraticShiftFormulaOpenTask",
    data: makeTaskData(base),
    source: {
      variant: isOriginal ? "original" : "random",
      exam: "MATURA",
      session: isOriginal ? "2026_MAJ" : undefined,
      taskNumber: "14",
      basedOn: "MMAP-P0-100-A-2605-14",
    },
  };
}

export function renderTask({ data }) {
  const shiftLatex = shiftedArgument(data.shift);
  return <div style={{ display: "grid", gap: 10 }}><p>Wykres funkcji kwadratowej <InlineMath math="f" /> ma wierzchołek <InlineMath math={`W=(${data.p},${data.q})`} />. Funkcję <InlineMath math="g" /> określono wzorem</p><BlockMath math={`g(x)=f(${shiftLatex}).`} /><p>Jednym z miejsc zerowych funkcji <InlineMath math="g" /> jest <InlineMath math={`x=${data.gZero}`} />. Wyznacz wzór funkcji <InlineMath math="f" /> w postaci ogólnej.</p></div>;
}

function getRaw(value) {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    if (value.raw && typeof value.raw === "object") return getRaw(value.raw);
    for (const key of ["raw", "value", "text", "expr", "equation", "latex"]) {
      if (typeof value[key] === "string" && value[key].trim()) return value[key];
    }
    return "";
  }
  return String(value ?? "");
}

function previewLatex(value) {
  return getRaw(value).replace(/([+-]?\d+)\/(\d+)/g, "\\frac{$1}{$2}");
}

function normalizeExpression(value) {
  let expression = getRaw(value)
    .trim()
    .replace(/\u2212/g, "-")
    .replace(/\\left/g, "")
    .replace(/\\right/g, "")
    .replace(/\\cdot/g, "*")
    .replace(/\\,/g, "")
    .replace(/\^{\s*2\s*}/g, "^2")
    .replace(/(\d),(\d)/g, "$1.$2");

  let previous;
  do {
    previous = expression;
    expression = expression.replace(
      /\\(?:d?frac)\{([+-]?\d+)\}\{([+-]?\d+)\}/g,
      "$1/$2",
    );
  } while (expression !== previous);

  return expression
    .replace(/^f\(x\)=/i, "")
    .replace(/^y=/i, "")
    .replace(/\s+/g, "")
    .replace(/[{}]/g, "");
}

function safeEvaluate(value, x) {
  let expression = normalizeExpression(value);
  if (!expression || /[^0-9x+\-*/().^]/i.test(expression)) return NaN;
  expression = expression
    .replace(/\^/g, "**")
    .replace(/(\d|\))(?=x|\()/g, "$1*")
    .replace(/x(?=\()/g, "x*")
    .replace(/x/g, `(${x})`);
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${expression});`)();
    return Number.isFinite(result) ? result : NaN;
  } catch {
    return NaN;
  }
}

function equivalentToFunction(value, data) {
  return [-3, -1, 0, 1, 2, 4].every((x) => {
    const actual = safeEvaluate(value, x);
    const expected = data.A * x * x + data.B * x + data.C;
    return Number.isFinite(actual) && Math.abs(actual - expected) < 1e-8;
  });
}

function canonicalTemplateCorrect(value, data) {
  const actual = normalizeExpression(value).replace(/\*/g, "");
  const expected = `a(${shiftedVariable(data.p)})^2${signed(data.q)}`;
  return actual === expected;
}

function canonicalFormulaCorrect(value, data) {
  const normalized = normalizeExpression(value);
  return normalized.includes("(x") && normalized.includes(")^2") && equivalentToFunction(value, data);
}

function generalFormulaCorrect(value, data) {
  const normalized = normalizeExpression(value);
  return normalized.includes("x^2") && !/[()]/.test(normalized) && equivalentToFunction(value, data);
}

export function renderInput(_task, value = {}, onChange, disabled) {
  const set = (key, next) => onChange?.({ ...value, [key]: next });
  const Preview = ({ prefix, fieldValue }) => {
    const raw = getRaw(fieldValue);
    return raw ? (
      <div style={{ marginTop: 2 }}>
        <strong>Podgląd:</strong> <InlineMath math={`${prefix}${previewLatex(raw)}`} />
      </div>
    ) : null;
  };
  const equationField = (key, prefix, placeholder, ariaLabel) => (
    <div style={{ display: "grid", gap: 6 }}>
      <div><InlineMath math={prefix} /></div>
      <div>
        <EquationInput
          value={value[key] || ""}
          onChange={(next) => set(key, next)}
          disabled={disabled}
          placeholder={placeholder}
          showPreview={false}
          ariaLabel={ariaLabel}
        />
      </div>
      <Preview prefix={prefix} fieldValue={value[key]} />
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 16, maxWidth: 680 }}>
      <OpenTaskScoringHint variant="optional" />
      <div style={{ display: "grid", gap: 6 }}>
        <strong>Krok 1. Zapisz postać kanoniczną wynikającą z wierzchołka. Pozostaw współczynnik <InlineMath math="a" />.</strong>
        {equationField("canonicalTemplate", "f(x)=", "np. a(x-3)^2-2", "postać kanoniczna ze współczynnikiem a")}
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        <strong>Krok 2 (opcjonalny). Podaj wynikające z warunku miejsce zerowe funkcji <InlineMath math="f" />.</strong>
        <span>Możesz pominąć ten etap, jeżeli wyznaczasz współczynnik <InlineMath math="a" /> bezpośrednio ze wzoru funkcji <InlineMath math="g" />.</span>
        <div><InlineMath math="x_0=" /></div>
        <div>
          <FractionInput
            value={value.fZero || ""}
            onChange={(next) => set("fZero", next)}
            disabled={disabled}
            label="miejsce zerowe funkcji f"
            showPreview={false}
          />
        </div>
        <Preview prefix="x_0=" fieldValue={value.fZero} />
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        <strong>Krok 3. Wyznacz <InlineMath math="a" /> i zapisz pełną postać kanoniczną.</strong>
        {equationField("canonicalFormula", "f(x)=", "np. 1/2(x-3)^2-2", "pełna postać kanoniczna")}
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        <strong>Krok 4. Zapisz wzór funkcji w postaci ogólnej.</strong>
        {equationField("generalFormula", "f(x)=", "np. 1/2x^2-3x+5/2", "postać ogólna funkcji")}
      </div>
    </div>
  );
}

const numberFrom = (value) => {
  const raw = String(value?.raw ?? value ?? "").trim().replace(",", ".");
  if (/^-?\d+\/\d+$/.test(raw)) {
    const [numerator, denominator] = raw.split("/").map(Number);
    return numerator / denominator;
  }
  return Number(raw);
};

export function checkAnswer({ data }, value = {}) {
  const templateOk = canonicalTemplateCorrect(value.canonicalTemplate, data);
  const expectedFZero = data.gZero + data.shift;
  const fZeroOk = Math.abs(numberFrom(value.fZero) - expectedFZero) < 1e-9;
  const canonicalOk = canonicalFormulaCorrect(value.canonicalFormula, data);
  const generalOk = generalFormulaCorrect(value.generalFormula, data);
  const score = generalOk
    ? 4
    : [templateOk, fZeroOk, canonicalOk].filter(Boolean).length;
  return {
    correct: score === 4,
    score,
    maxScore: 4,
    details: {
      "postać kanoniczna ze współczynnikiem a": templateOk,
      "miejsce zerowe funkcji f": fZeroOk,
      "pełna postać kanoniczna": canonicalOk,
      "postać ogólna": generalOk,
    },
  };
}

export function renderAnswer({ data }) {
  const fZero = data.gZero + data.shift;
  return (
    <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
      <BlockMath math={`f(x)=a(${shiftedVariable(data.p)})^2${signed(data.q)}`} />
      <BlockMath math={`x_0=${fZero}`} />
      <BlockMath math={`f(x)=${fracLatex(data.a)}(${shiftedVariable(data.p)})^2${signed(data.q)}`} />
      <BlockMath math={`f(x)=${quadraticLatex(data)}`} />
    </div>
  );
}

export function renderUserAnswer(value = {}) {
  const rows = [
    ["Postać kanoniczna ze współczynnikiem a", "f(x)=", getRaw(value.canonicalTemplate)],
    ["Miejsce zerowe funkcji f", "x_0=", getRaw(value.fZero)],
    ["Pełna postać kanoniczna", "f(x)=", getRaw(value.canonicalFormula)],
    ["Postać ogólna", "f(x)=", getRaw(value.generalFormula)],
  ];
  return (
    <div style={{ display: "grid", gap: 8, textAlign: "left" }}>
      {rows.map(([label, prefix, raw]) => (
        <div key={label}>
          <strong>{label}:</strong>{" "}
          <InlineMath math={`${prefix}${raw ? previewLatex(raw) : "\\text{–}"}`} />
        </div>
      ))}
    </div>
  );
}

export function buildExplanationModel(data) {
  const shiftedVertex = data.p - data.shift;
  const zeroDifference = evaluatedDifference(data.gZero, shiftedVertex);
  const zeroSquare = (data.gZero - shiftedVertex) ** 2;
  const canonicalFactor = shiftedVariable(data.p);
  const shiftedFactor = shiftedVariable(shiftedVertex);
  const argument = shiftedArgument(data.shift);
  const substitutedInner = data.p === 0
    ? `(${argument})`
    : `(${argument})${data.p > 0 ? `-${data.p}` : `+${Math.abs(data.p)}`}`;
  const expandedSquare = `x^2${signed(-2 * data.p, "x")}${signed(data.p * data.p)}`;
  const distributed = `${coefficient(data.A, "x^2")}${signed(data.B, "x")}${signed(data.a * data.p * data.p)}${signed(data.q)}`;
  const zeroCoefficient = zeroSquare === 1 ? "a" : `${zeroSquare}a`;

  return {
    canonical: `f(x)=a(${canonicalFactor})^2${signed(data.q)}`,
    substitution: `g(x)=f(${argument})=a(${substitutedInner})^2${signed(data.q)}=a(${shiftedFactor})^2${signed(data.q)}`,
    zeroSubstitution: `0=g(${data.gZero})=a(${zeroDifference})^2${signed(data.q)}=${zeroCoefficient}${signed(data.q)}`,
    solveCoefficient: `${zeroCoefficient}=${fracLatex(-data.q)}\\Longrightarrow a=${fracLatex(data.a)}`,
    squareIdentity: `(${canonicalFactor})^2=${expandedSquare}`,
    expandedSubstitution: `f(x)=${fracLatex(data.a)}(${canonicalFactor})^2${signed(data.q)}=${fracLatex(data.a)}(${expandedSquare})${signed(data.q)}`,
    distribution: `f(x)=${distributed}`,
    finalFormula: `f(x)=${quadraticLatex(data)}`,
  };
}

export function renderExplanation({ data }) {
  const model = buildExplanationModel(data);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <p><strong>Krok 1.</strong> Korzystamy z wierzchołka i zapisujemy funkcję <InlineMath math="f" /> w postaci kanonicznej.</p>
      <BlockMath math={model.canonical} />

      <p><strong>Krok 2.</strong> Zgodnie z definicją funkcji <InlineMath math="g" /> w miejsce argumentu funkcji <InlineMath math="f" /> podstawiamy <InlineMath math={shiftedArgument(data.shift)} />, a następnie upraszczamy wyrażenie w nawiasie.</p>
      <BlockMath math={model.substitution} />

      <p><strong>Krok 3.</strong> Liczba <InlineMath math={String(data.gZero)} /> jest miejscem zerowym funkcji <InlineMath math="g" />, więc <InlineMath math={`g(${data.gZero})=0`} />. Podstawiamy ją do otrzymanego wzoru i wyznaczamy współczynnik <InlineMath math="a" />.</p>
      <BlockMath math={model.zeroSubstitution} />
      <BlockMath math={model.solveCoefficient} />

      <p><strong>Krok 4.</strong> Współczynnik <InlineMath math="a" /> jest taki sam w funkcjach <InlineMath math="f" /> i <InlineMath math="g" />. Wracamy do wzoru funkcji <InlineMath math="f" /> i korzystamy ze wzoru skróconego mnożenia.</p>
      <BlockMath math={model.squareIdentity} />
      <BlockMath math={model.expandedSubstitution} />
      <BlockMath math={model.distribution} />

      <p><strong>Wniosek.</strong> Wzór funkcji <InlineMath math="f" /> w postaci ogólnej ma postać:</p>
      <BlockMath math={model.finalFormula} />
    </div>
  );
}
